// src/components/ShopModal.tsx
import React from "react";
import { Modal } from "./Modal";
import type { Sku } from "../customization/pricing";
import { PRICES } from "../customization/pricing";

// Represent the basket aggregated by category
export type Basket = {
  // totals
  timeSec: number;      // total seconds selected
  hintCount: number;    // total hints selected
  // star totals (for payment sheet)
  timeStars: number;
  hintStars: number;
  // stacks to support "−" (remove last added pack)
  timeStack: Sku[];     // only time SKUs here
  hintStack: Sku[];     // only hint SKUs here
};

type Props = {
  open: boolean;
  onClose: () => void;
  balanceSec: number;
  balanceHints: number;
  onCheckout: (basket: Basket) => void;
};

const TIME_SKUS: Sku[] = ["time60", "time120", "time300"];
const HINT_SKUS: Sku[] = ["hint1", "hint3", "hint5"];

// Smallest units for + buttons in basket rows
const TICK_TIME: Sku = "time60";
const TICK_HINT: Sku = "hint1";

export function ShopModal({ open, onClose, balanceSec, balanceHints, onCheckout }: Props) {
  const [basket, setBasket] = React.useState<Basket>({
    timeSec: 0,
    hintCount: 0,
    timeStars: 0,
    hintStars: 0,
    timeStack: [],
    hintStack: [],
  });

  React.useEffect(() => {
    if (!open) {
      // reset every time it closes (remove if you prefer persistence)
      setBasket({
        timeSec: 0, hintCount: 0,
        timeStars: 0, hintStars: 0,
        timeStack: [], hintStack: [],
      });
    }
  }, [open]);

  const addSku = (sku: Sku) => {
    const p = PRICES[sku];
    if (!p) return;

    setBasket(b => {
      const next = { ...b, timeStack: [...b.timeStack], hintStack: [...b.hintStack] };

      if (p.grant.type === "time") {
        next.timeSec += p.grant.amount;
        next.timeStars += p.stars;
        next.timeStack.push(sku);
      } else {
        next.hintCount += p.grant.amount;
        next.hintStars += p.stars;
        next.hintStack.push(sku);
      }

      return next;
    });
  };

  // Remove the last added pack for a category (LIFO)
  const popLast = (kind: "time" | "hint") => {
    setBasket(b => {
      const next = { ...b, timeStack: [...b.timeStack], hintStack: [...b.hintStack] };

      if (kind === "time" && next.timeStack.length) {
        const sku = next.timeStack.pop() as Sku;
        const p = PRICES[sku];
        next.timeSec = Math.max(0, next.timeSec - p.grant.amount);
        next.timeStars = Math.max(0, next.timeStars - p.stars);
      }
      if (kind === "hint" && next.hintStack.length) {
        const sku = next.hintStack.pop() as Sku;
        const p = PRICES[sku];
        next.hintCount = Math.max(0, next.hintCount - p.grant.amount);
        next.hintStars = Math.max(0, next.hintStars - p.stars);
      }

      return next;
    });
  };

  // Add the smallest tick unit for a category
  const addTick = (kind: "time" | "hint") => {
    addSku(kind === "time" ? TICK_TIME : TICK_HINT);
  };

  // Clear a category entirely
  const clearKind = (kind: "time" | "hint") => {
    setBasket(b => ({
      ...b,
      ...(kind === "time"
        ? { timeSec: 0, timeStars: 0, timeStack: [] }
        : { hintCount: 0, hintStars: 0, hintStack: [] }),
    }));
  };

  const totalItems = (basket.timeStack.length + basket.hintStack.length);

  const proceed = () => {
    if (totalItems <= 0) return;
    onCheckout(basket);
  };

  return (
    <Modal open={open} onClose={onClose} title="Shop">
      <div style={{ display: "grid", gap: 12 }}>
        {/* Balances */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div style={{ padding: "8px 12px", background: "rgba(255,255,255,0.06)", borderRadius: 10 }}>
            <div style={{ opacity: 0.9 }}>Your Time</div>
            <div style={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{balanceSec}s</div>
          </div>
          <div style={{ padding: "8px 12px", background: "rgba(255,255,255,0.06)", borderRadius: 10 }}>
            <div style={{ opacity: 0.9 }}>Your Hints</div>
            <div style={{ fontWeight: 800, fontVariantNumeric: "tabular-nums" }}>{balanceHints}</div>
          </div>
        </div>

        {/* Time packs */}
        <section>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Extra Time</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
            {TIME_SKUS.map(sku => (
              <button
                key={sku}
                className="tab-button"
                style={{ width: "100%" }}
                onClick={() => addSku(sku)}
                title="Add to basket"
              >
                {PRICES[sku].title}
              </button>
            ))}
          </div>
        </section>

        {/* Hints */}
        <section>
          <div style={{ fontWeight: 800, marginBottom: 8 }}>Super Hints</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10 }}>
            {HINT_SKUS.map(sku => (
              <button
                key={sku}
                className="tab-button"
                style={{ width: "100%" }}
                onClick={() => addSku(sku)}
                title="Add to basket"
              >
                {PRICES[sku].title}
              </button>
            ))}
          </div>
        </section>

        {/* Basket — aggregated to two lines */}
        <section>
          <div style={{ fontWeight: 800, margin: "4px 0 8px" }}>Your basket</div>

          {totalItems === 0 ? (
            <div style={{ opacity: 0.8 }}>Add items above to build your basket.</div>
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {/* Time row (only if any) */}
              {basket.timeSec > 0 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    gap: 8,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 10,
                    padding: "8px 10px",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>Extra Time</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button className="tab-button" onClick={() => popLast("time")} title="Remove last time pack">−</button>
                    <div style={{ minWidth: 60, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                      {basket.timeSec}s
                    </div>
                    <button className="tab-button" onClick={() => addTick("time")} title="+60s">+</button>
                    <button className="tab-button" onClick={() => clearKind("time")} title="Clear">×</button>
                  </div>
                </div>
              )}

              {/* Hint row (only if any) */}
              {basket.hintCount > 0 && (
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr auto",
                    alignItems: "center",
                    gap: 8,
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    borderRadius: 10,
                    padding: "8px 10px",
                  }}
                >
                  <div style={{ fontWeight: 600 }}>Super Hints</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <button className="tab-button" onClick={() => popLast("hint")} title="Remove last hint pack">−</button>
                    <div style={{ minWidth: 28, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                      {basket.hintCount}
                    </div>
                    <button className="tab-button" onClick={() => addTick("hint")} title="+1 hint">+</button>
                    <button className="tab-button" onClick={() => clearKind("hint")} title="Clear">×</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </section>

        {/* Footer actions */}
        <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button className="tab-button" onClick={onClose}>Close</button>
          <button
            className="agree-button"
            disabled={totalItems <= 0}
            onClick={proceed}
            title={totalItems <= 0 ? "Basket is empty" : "Proceed to payment"}
          >
            Checkout
          </button>
        </div>
      </div>
    </Modal>
  );
}
